import { useState, useEffect, useRef } from 'react';
import { TrendingUp, Users, Award, Clock } from 'lucide-react';

const stats = [
  { icon: TrendingUp, value: 200, suffix: '+', label: 'Projects Completed', color: '#3B82F6' },
  { icon: Users, value: 100, suffix: '+', label: 'Happy Clients', color: '#8B5CF6' },
  { icon: Award, value: 99, suffix: '%', label: 'Client Satisfaction', color: '#F97316' },
  { icon: Clock, value: 24, suffix: '/7', label: 'Support Available', color: '#10B981' },
];

const Stats = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [counts, setCounts] = useState(stats.map(() => 0));
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.3 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (isVisible) {
      stats.forEach((stat, index) => {
        let start = 0;
        const end = stat.value;
        const duration = 2000;
        const increment = end / (duration / 16);

        const counter = setInterval(() => {
          start += increment;
          if (start >= end) {
            setCounts(prev => {
              const newCounts = [...prev];
              newCounts[index] = end;
              return newCounts;
            });
            clearInterval(counter);
          } else {
            setCounts(prev => {
              const newCounts = [...prev];
              newCounts[index] = Math.floor(start);
              return newCounts;
            });
          }
        }, 16);
      });
    }
  }, [isVisible]);

  return (
    <section ref={sectionRef} className="relative py-20" style={{ background: 'var(--bg-dark)' }}>
      {/* Background */}
      <div className="absolute inset-0 bg-gradient-to-b from-[#12121A] via-[#0A0A0F] to-[#12121A]"></div>
      
      {/* Accent Orbs */}
      <div className="absolute top-1/2 left-1/4 w-[300px] h-[300px] bg-[#3B82F6]/10 rounded-full blur-[120px]"></div>
      <div className="absolute top-1/2 right-1/4 w-[300px] h-[300px] bg-[#8B5CF6]/10 rounded-full blur-[120px]"></div>

      <div className="relative max-w-[1400px] mx-auto px-[7.6923%]">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
          {stats.map((stat, index) => {
            const Icon = stat.icon;
            return (
              <div
                key={index}
                data-testid={`stat-${index}`}
                className="group relative bg-[#12121A]/80 backdrop-blur-sm border border-white/10 rounded-2xl p-6 md:p-8 text-center transition-all duration-500 hover:border-white/20 hover:-translate-y-2"
              >
                {/* Hover Glow */}
                <div 
                  className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                  style={{ background: `radial-gradient(circle at center, ${stat.color}10, transparent 70%)` }}
                ></div>

                <div className="relative">
                  {/* Icon */}
                  <div 
                    className="w-14 h-14 rounded-xl mx-auto mb-4 flex items-center justify-center transition-transform duration-300 group-hover:scale-110"
                    style={{ background: `${stat.color}15`, border: `1px solid ${stat.color}30` }}
                  >
                    <Icon size={26} style={{ color: stat.color }} />
                  </div>

                  {/* Value */}
                  <div 
                    className="text-4xl md:text-5xl font-bold mb-2 transition-all duration-300"
                    style={{ 
                      background: `linear-gradient(135deg, ${stat.color}, ${stat.color}99)`,
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent'
                    }}
                  >
                    {counts[index]}{stat.suffix}
                  </div>

                  {/* Label */}
                  <div className="text-white/60 text-sm font-medium">
                    {stat.label}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default Stats;
